<template>
  <div ref="borderItem" class="wrap-editor-border-item">
    <div class="wrap-drop-panel">
      <div class="content">
        <label class="label"> Style </label>
        <GfDropdown
          class="custom--hover"
          f-type="simple"
          :options="styleOption"
          :value="border.value.style"
          :dropdown-styling="{ width: '120px' }"
          :dropdown-styling-option="{ width: '120px', 'z-index': '99' }"
          @selected="onSelectStyle" />
      </div>
      <div class="content">
        <label class="label"> Thickness </label>
        <GfInputUnit
          :required="true"
          :min="0"
          :input-styling="{ width: '120px' }"
          :value="borderValue.thickness"
          :units="units"
          @onChange="changeThickness" />
      </div>
      <div class="content">
        <label class="label"> Color </label>
        <ColorItem
          :is-active="showColorPalette"
          :color="border.value.color"
          :is-can-change-color="true"
          @click="showColorPalette = !showColorPalette" />
        <div v-if="showColorPalette" class="color--palette" :style="index == 0 ? 'top: 40px; bottom: 0' : ''">
          <GfColorPalette
            :color="border.value.color"
            @pickColor="onApplyColorPalette"
            @editColorStyle="onEditColorStyle"
            @onClosePallete="showColorPalette = false" />
        </div>
      </div>
    </div>
    <Teleport to="#preview-border">
      <div ref="previewBorder" class="preview" :style="previewStyling">
        <div class="preview-header">
          <label class="preview--label gf_truncate">Preview {{ border.name }}</label>
        </div>
        <div class="preview--content">
          <div class="content" :style="contentPreviewStyling">
            <div :key="borderP" class="border" :style="borderP"></div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { cloneDeep } from '@/utils/common';
import { BorderSegmentOptions, BorderStyleOptions, dropPresetValueStyle } from '../../constants/styleConstant';
import { mapState, mapGetters, mapMutations } from 'vuex';
import { mutations } from '@modules/editor-sidebar';
import { convertBorderOptionToCssObject } from '@modules/global-style';
import { COLOR_KEYS_MAP } from '@/constants/global-style';
import ColorItem from '@/core/components/base/color/ColorItem.vue';

interface DataInterface {
  styleOption: Array<{
    name: string;
    value: string;
  }>;
  units: Array<{
    name: string;
    value: string;
  }>;
  segmentOptions: Array<{
    value: string;
    icon: {
      url: string;
      position: string;
    };
  }>;
  showColorPalette: boolean;
  borderValue: any;
  previewStyling: any;
  showPreview: number;
  showInput: boolean;
  showMenuBarAction: boolean;
}

export default defineComponent({
  name: 'BorderItem',
  components: {
    ColorItem,
  },
  props: {
    index: {
      type: Number,
      default: 0,
    },
    border: {
      type: Object,
      default: () => {},
    },
    toggleEvent: {},
    isReadyToSave: {
      type: Boolean,
      default: true,
    },
    positionTopPreview: {
      type: Number,
      default: 0,
    },
  },
  data: (): DataInterface => ({
    showInput: false,
    showMenuBarAction: false,
    showPreview: 0,
    borderValue: {},
    styleOption: BorderStyleOptions,
    previewStyling: {},
    units: [
      {
        name: 'px',
        value: 'px',
      },
    ],
    segmentOptions: BorderSegmentOptions,
    showColorPalette: false,
  }),
  computed: {
    ...mapState('sidebar', {
      currentPreset: (state) => (state as any).currentPreset,
    }),
    ...mapGetters('sidebar', {
      currentPresetColor: 'getCurrentPresetColor',
    }),
    contentPreviewStyling() {
      return {
        background: this.currentPresetColor.value.neutral.color1,
      };
    },
    borderP() {
      const cssValue = Object.values(convertBorderOptionToCssObject(this.border))[0];

      return `${cssValue} border-top: 0 !important;`;
    },
  },
  watch: {
    border: {
      handler() {
        this.mergeBorderSetting();
      },
      deep: true,
    },
    positionTopPreview(val) {
      this.$nextTick(() => {
        let top = val;
        const previewHeight = this.$refs.previewBorder?.getBoundingClientRect().height || 112;
        if (val + 100 + previewHeight > window.innerHeight) {
          top = window.innerHeight - previewHeight - 120;
        } else if (val < 0) {
          top = 100;
        }
        this.previewStyling = {
          top: top + 'px',
        };
      });
    },
  },
  mounted() {
    this.mergeBorderSetting();
  },
  methods: {
    ...mapMutations('sidebar', {
      stepPreset: mutations.SET_STEP_PRESET,
    }),
    inputChange(): void {
      this.showInput = false;
    },
    mergeBorderSetting(): void {
      const borderValue = cloneDeep(this.border.value);

      this.borderValue.style = borderValue.style;
      this.borderValue.thickness = borderValue?.thickness?.replace('px', '');
      this.borderValue.dash = borderValue?.dash?.replace('px', '');
      this.borderValue.gap = borderValue?.gap?.replace('px', '');
      this.borderValue.dashCap = borderValue.dashCap;
      this.borderValue.color = borderValue.color;
    },
    onSelectStyle(select: { name: string; value: string }): void {
      this.border.value.style = select.value;
    },
    changeThickness(thinkNess: { value: number; unit: string }): void {
      if (!thinkNess.value) {
        thinkNess.value = 0;
      }
      this.border.value.thickness = thinkNess.value + thinkNess.unit;
    },
    changeDash(dash: { value: number; unit: string }): void {
      this.border.value.dash = dash.value + dash.unit;
    },
    changeGap(gap: { value: number; unit: string }): void {
      this.border.value.gap = gap.value + gap.unit;
    },
    onSegmentChanged(segment: { id: number; value: string }): void {
      this.border.value.dashCap = segment.value;
    },
    onApplyColorPalette(color: any): void {
      this.border.value.color = `var(--${COLOR_KEYS_MAP[color.name]})`;
      this.showColorPalette = false;
    },
    onEditColorStyle() {
      if (!this.isReadyToSave) {
        return;
      }
      this.stepPreset({
        next: {
          type: dropPresetValueStyle.colorStyle,
          target: {},
        },
        prev: {
          type: dropPresetValueStyle.borderStyle,
          target: this.border,
        },
      });
    },
  } as any,
});
</script>

<style scoped lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.show {
  margin-left: 20px;
  display: block;
}

.hide {
  display: none;
  margin-left: 20px;
}

.preview {
  position: absolute;
  right: 0;
  top: 0;
  width: 278px;
  height: 112px;
  background: #494949;
  left: 295px;
  box-shadow: 0px 12px 38px rgba(34, 34, 34, 0.0393604), 0px 5.01331px 15.8755px rgba(34, 34, 34, 0.056545),
    0px 2.68036px 8.4878px rgba(34, 34, 34, 0.07), 0px 1.50259px 4.75819px rgba(34, 34, 34, 0.083455),
    0px 0.798012px 2.52704px rgba(34, 34, 34, 0.10064), 0px 0.332071px 1.05156px rgba(34, 34, 34, 0.14);
  border-radius: 3px;

  .preview--content {
    .content {
      background: #f2e8f7;
      border-radius: 3px;
      height: 48px;
      margin: 12px 12px 12px 12px;
      @include flex-center;

      .border {
        width: 82%;
      }
    }
  }

  .preview-header {
    background: #252525;
    border-radius: 3px 3px 0 0;
    height: 40px;

    .preview--label {
      font-weight: 500;
      font-size: 14px;
      line-height: 16px;
      color: #e2e2e2;
      flex: none;
      order: 0;
      flex-grow: 0;
      padding: 12px 12px 12px 12px;
    }
  }

  @media (max-width: 1300px) {
    left: 265px;
  }
}

.wrap-editor-border-item {
  position: relative;
  border-radius: $radius;
  -moz-border-radius: $radius;
  -webkit-border-radius: $radius;
  background: $black;
  color: $highlight2;
  font-size: 12px;
  padding: 8px;

  border: 1px solid #494949;
  margin-top: -3px;
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;

  .wrap-drop-panel {
    .content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      :deep(.wrapper) {
        margin-right: 0 !important;
      }
    }

    .label {
      font-style: normal;
      font-weight: 400;
      font-size: 12px;
      line-height: 14px;
      color: #aaaaaa;
      padding: 0;
    }
  }
}

.wrap-drop-bar {
  background: $black3;
  cursor: pointer;
  position: relative;
  height: 32px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-left: 10px;

  .lbl-input {
    height: 24px;
    font-size: 12px;
    background-color: map-get($hybrid-shade-dark, '400');
    border: 1px solid transparent;
    color: $highlight2;
    cursor: pointer;
    border-radius: $radius;
    -moz-border-radius: $radius;
    -webkit-border-radius: $radius;
    width: 55%;

    &[data-input-focus='true'] {
      padding-left: 5px;
      margin-left: 8px;
    }

    &:focus {
      cursor: text;
      border: 1px solid map-get($primary-blue, '275');
    }

    &:hover {
      border: 1px solid #d6d6d6;
    }
  }

  .drop-duplicate {
    padding: 7px;
    margin-right: 1px;
    height: 28px;
    width: 28px;
    border-radius: $radius;
    -moz-border-radius: $radius;
    -webkit-border-radius: $radius;

    position: absolute;
    right: 74px;
    top: 6px;

    &:hover {
      background: map-get($hybrid-shade-dark, '300');
    }
  }

  .drop-delete {
    padding: 7px;
    margin-right: 1px;
    height: 28px;
    width: 28px;
    border-radius: $radius;
    -moz-border-radius: $radius;
    -webkit-border-radius: $radius;
    position: absolute;
    right: 40px;
    top: 6px;

    &:hover {
      background: map-get($accent-color-red, '400');
    }
  }

  .drop-title {
    color: $textDarkHigh;
    font-weight: 400;
    line-height: 14px;
    padding-bottom: 0;
  }

  .drop-action {
    position: absolute;
    right: 10px;
    top: 5px;

    .link-col-action {
      display: flex;
      justify-content: center;
      padding: 7px;
      height: 28px;
      width: 28px;
      border-radius: $radius;
      -moz-border-radius: $radius;
      -webkit-border-radius: $radius;

      &:hover {
        background: map-get($hybrid-shade-dark, '300');
      }
    }
  }
}

.wrap-drop-panel {
  .content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    position: relative;
  }

  .label {
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    color: #aaaaaa;
  }

  .button {
    background-image: url('/editor/static/icons/transparent-color.svg');
    background-size: cover;
    border: 1px solid #494949;
    box-sizing: border-box;
    border-radius: 4px;
    width: 32px;
    height: 32px;
    padding: unset;

    &--box {
      width: 100%;
      height: 100%;
      border-radius: 3px;
    }
  }

  .color--palette {
    position: absolute;
    bottom: 40px;
    z-index: 9;
  }
}
</style>
